<template>
    <component
        :is="componentStyle.comp"
        v-bind="{ ...componentStyle.props }"
        v-model="show"
        @click:outside="$vuetify.breakpoint.mdAndUp ? close() : null"
    >
        <v-form
            ref="form"
            @submit.prevent="validate()"
            class="contents"
            v-model="valid"
        >
            <!-- instead of v-model, I should just have it be a link that creates an empty message in a conversation so drafts work and to save on form.recipients complexity -->
            <ConversationToolbar
                v-model="form.recipients"
                :conversation="conversation"
                :thread="thread"
                :crmMode="crmMode"
                :rules="rules.recipients"
                :disabled="form.processing || messageSending"
                :error="errors.recipients"
                :crmClient="crmClient"
                :searching="searching"
                @changeThread="$emit('changeThread', $event)"
                @openCrm="$emit('openCrm')"
                @close="close()"
                @input="createConversation($event)"
            />

            <v-divider />

            <div
                v-if="loading || crmLoading"
                class="w-full h-full flex justify-center align-center"
            >
                <v-progress-circular
                    indeterminate
                    color="primary"
                ></v-progress-circular>
            </div>

            <ConversationMessages
                ref="messages"
                :key="messagesKey"
                :cursor="cursor"
                :conversation="conversation"
                :conversationUser="conversationUser"
                :thread="thread"
                :messages="messages"
                :messageCount="messageCount"
                :participants="conversation?.participants"
                :crmMode="crmMode"
                :searching="searching"
                @changeThread="$emit('changeThread', $event)"
                @handleUnread="$emit('handleUnread', $event)"
                @loading="[(loading = $event), $emit('loading', $event)]"
            >
                <div
                    v-if="newMessageAvailable"
                    class="w-min mx-auto flex justify-center"
                    style="position: sticky; top: 94%; z-index: 60"
                >
                    <v-chip
                        pill
                        color="primary"
                        @click="viewLatest(newMessageAvailable)"
                        ><v-icon small class="mr-1">arrow_downward</v-icon
                        ><b>New Message</b></v-chip
                    >
                </div>
            </ConversationMessages>

            <div id="chat-box" class="px-4 mt-4 safe-bottom">
                <v-text-field
                    v-if="channel == 'email'"
                    v-model="form.subject"
                    dense
                    outlined
                    :error-messages="errors.subject"
                    :rules="rules.subject"
                    :disabled="!!form.thread_id"
                    label="Subject"
                    hide-details="auto"
                    class="mb-2"
                ></v-text-field>
                <TipTap
                    ref="content"
                    v-model="form.content"
                    :key="tipTapKey"
                    :processing="form.processing || messageSending"
                    :valid="valid && hasContent && !hasDeletedParticipants"
                    :characterLimit="characterLimit"
                    :channel="channel"
                    :editorType="
                        ['chat', 'email'].includes(channel) ? 'html' : 'text'
                    "
                    :autofocus="!conversation.create"
                    :crmMode="crmMode"
                    :conversationId="conversation.id"
                    :client="form.recipients[0]"
                    :thread="thread"
                    :message="assistantMessage"
                    :autoDraft="!!conversationUser?.auto_drafted_at"
                    @showMessage="$emit('showMessage')"
                    @error="assistantError($event)"
                    @contentTrimmed="contentTrimmedAlert()"
                    @changeChannel="changeChannel($event)"
                    @input="saveDraft($event)"
                    @submit="validate()"
                />
                <v-input
                    ref="contentInput"
                    :rules="rules.content"
                    :value="form.content"
                    :error-messages="draftErrors"
                    hide-details="auto"
                    class="px-6"
                />
            </div>
        </v-form>
    </component>
</template>

<script>
import { Link } from "@inertiajs/vue2";
import { VBottomSheet } from "vuetify/lib";
import ConversationToolbar from "./ConversationToolbar.vue";
import ConversationMessages from "./ConversationMessages.vue";
import axios from "axios";
//import TinyEditor from "./../TinyEditor.vue";
import TipTap from "../Editor/TipTap.vue";

export default {
    components: {
        Link,
        VBottomSheet,
        ConversationToolbar,
        ConversationMessages,
        //TinyEditor,
        TipTap,
    },
    props: {
        value: {
            type: String,
            default: "",
        },
        cursor: {
            type: String,
            default: null,
        },
        conversation: {
            type: Object,
            default: () => {},
        },
        conversationUser: {
            type: Object,
            default: () => {},
        },
        thread: {
            type: Object,
            default: () => {},
        },
        messages: {
            type: Array,
            default: () => [],
        },
        messageCount: {
            type: Number,
            default: 0,
        },
        newMessageAvailable: {
            type: Object,
            default: () => null,
        },
        errors: {
            type: Object,
            default: () => {},
        },
        crmMode: {
            type: Boolean,
            default: false,
        },
        crmClient: {
            type: Object,
            default: () => {},
        },
        crmLoading: {
            type: Boolean,
            default: false,
        },
        action: {
            type: String,
            default: "",
        },
        searching: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            show: true,
            loading: true,
            messageSending: false,
            setDraft: null,
            draftErrors: [],
            rules: {
                content: [
                    //(v) => !!this.stripHtmlTags(v) || "Message required",
                    (v) =>
                        this.stripHtmlTags(v)?.length < 3000 || "Limit reached",
                    //(v) => !!v?.length || "Message required",
                ],
                subject: [(v) => !!v || "Subject required"],
                recipients: [(v) => !!v.length || "Recipient(s) required"],
            },
            form: this.$inertia.form({
                content: null,
                conversation_id: this.conversation?.id ?? null,
                recipients: [],
                subject: null,
                thread_id: this.thread?.id ?? null,
            }),
            valid: false,
            setChannel: "",
        };
    },
    methods: {
        createConversation(recipients) {
            if (!this.crmMode) return;
            this.$inertia.visit(
                route("conversation.search", {
                    recipients: recipients.map((data) => {
                        return {
                            messagable_id: data.id,
                            messagable_type: data.type,
                        };
                    }),
                    channel: this.channel,
                    draft: this.form.content,
                })
            );
        },
        changeChannel(channel) {
            this.setChannel = channel;
        },
        contentTrimmedAlert() {
            this.draftErrors = ["Content trimmed to match character limit"];
            setTimeout(() => {
                this.draftErrors = [];
            }, 3000);
        },
        assistantError(message) {
            this.draftErrors = [message];
            setTimeout(() => {
                this.draftErrors = [];
            }, 3000);
        },
        stripHtmlTags(html) {
            const doc = new DOMParser().parseFromString(html, "text/html");
            return doc.body.textContent || "";
        },
        close() {
            document.body.classList.remove("dialog-open-fix");
            this.show = false;
            this.$nextTick(() => {
                this.$emit("reset");
            });
        },
        scrollToBottom() {
            let el = document.getElementById("scroller");
            if (!el) return;
            el.scroll({ behavior: "instant", top: el.scrollHeight });
        },
        validate() {
            if (!this.$refs.form.validate()) return;
            this.saveDraft(null, true);
            this.scrollToBottom();
            this.sendMessage();
        },
        messageReceived(message) {
            this.$refs.messages.messageReceived(message);
        },
        viewLatest(message) {
            this.$inertia.get(
                route("conversation.show", {
                    conversation: this.conversation.id,
                    thread: message.thread_id ?? null,
                }),
                { crmMode: this.crmMode },
                {
                    preserveState: false,
                    only: [
                        "newMessageAvailable",
                        "conversation",
                        "thread",
                        "messages",
                        "unreadMessageCount",
                        "index",
                        "messageCount",
                        "crmClient",
                    ],
                }
            );
        },
        sendMessage() {
            this.form
                .transform((data) => ({
                    ...data,
                    recipients: data.recipients.map((r) => {
                        return { messagable_id: r.id, messagable_type: r.type };
                    }),
                    crmMode: this.crmMode,
                    channel: this.channel,
                    cursor: this.cursor, // this will cause the cursor to clear on message creation
                    search: this.searching, // this will cause the search to clear on message creation
                    //content: data.content.trim(),
                }))
                .post(route("message.store"), {
                    preserveState:
                        !this.conversation.create &&
                        !this.conversation.message_id &&
                        !this.conversation.cursor &&
                        !this.searching,
                    preserveScroll: true,
                    only:
                        this.conversation.create || this.conversation.message_id
                            ? [
                                  "conversation",
                                  "thread",
                                  "index",
                                  "messages",
                                  "messageCount",
                                  "unreadMessageCount",
                                  "newMessageAvailable",
                                  "crmClient",
                                  "searching",
                              ]
                            : [
                                  "messages",
                                  "conversation",
                                  "index",
                                  "crmClient",
                                  "searching",
                              ],
                    onSuccess: () => {
                        this.form.content = null;
                        this.rules = {};

                        if (this.$refs.content) {
                            this.$refs.content.editor.commands.clearContent();
                            this.$nextTick(() => {
                                this.$refs.content.editor.commands.focus();
                            });
                        }
                    },
                    onError: (errors) => {
                        console.log(errors);
                    },
                    onFinish: () => {
                        // This was just because crmClient is handled in the controller instead of the middleware, so crmClient will be missing
                        return;
                        if (
                            this.conversation.create ||
                            this.conversation.message_id
                        ) {
                            return;
                        }

                        if (!this.$page.props.errors) {
                            this.$inertia.reload({
                                only: [
                                    "conversation",
                                    "thread",
                                    "index",
                                    "messageCount",
                                ],
                            });
                        }

                        this.$refs.messages.previousMessages = [];
                        this.$refs.messages.nextMessages = [];
                    },
                });
        },
    },
    computed: {
        hasDeletedParticipants() {
            if (!this.conversation?.participants) return false;
            return this.conversation.participants.some((e) => e.trashed);
        },
        latestMessage() {
            if (!this.messages.length) return null;
            return this.messages[0];
        },
        assistantMessage() {
            if (this.channel == "email") {
                if (!this.thread?.id) return null;
                return (
                    this.messages.filter((e) => e.channel == "email")[0] ?? null
                );
            }
            return (
                this.messages.filter((e) =>
                    ["sms", "mms"].includes(e.channel)
                )[0] ?? null
            );
        },
        hasContent() {
            const content = this.stripHtmlTags(this.form.content);
            if (content == "null") return false;
            if (!content.trim().length) return false;
            return !!content.length;
        },
        channel() {
            if (!this.crmMode) return "chat";
            if (this.loading) return "";
            if (this.thread?.id) {
                return this.messages[0]?.channel ?? "sms";
            }
            if (this.setChannel) return this.setChannel;
            if (this.action) return this.action;
            /*if (this.messages.length) {
                return this.messages[0].channel;
            }*/
            return "sms";
        },
        messagesKey() {
            if (this.conversation.create) {
                return "createMessage";
            }

            if (this.conversation.message_id) {
                return this.conversation.message_id;
            }

            if (this.thread?.id) {
                return this.thread?.id;
            }

            return this.conversation.id;
        },
        tipTapKey() {
            return this.messagesKey + this.channel + this.setDraft;
        },
        componentStyle() {
            const mobile = this.$vuetify.breakpoint.smAndDown;
            return {
                comp: mobile ? "v-bottom-sheet" : "div",
                props: mobile
                    ? {
                          fullscreen: true,
                          persistent: true,
                          "content-class": `overflow-hidden safe-bottom white flex flex-column pb-4 ${
                              /*this.$page.props.crmClient?.id*/ false
                                  ? "pr-16"
                                  : ""
                          }`,
                      }
                    : {
                          style: "display: contents",
                      },
            };
        },
        characterLimit() {
            if (this.crmMode) {
                if (this.channel == "sms") {
                    return 320;
                }
                if (this.channel == "email") {
                    return 3000;
                }
            }
            return 3000;
        },
    },
    created() {
        this.saveDraft = this.debounce((value, cancel) => {
            if (this.loading) return;
            //if (!this.$refs.form?.validate("recipients")) return;
            if (this.conversation.create) return;
            if (this.messageSending) return;
            if (this.form.processing) return;
            if (!value?.length) value = null;
            const draft = this.conversationUser.draft;
            if (draft == value) return;

            axios
                .put(
                    `/conversation-user/${this.conversationUser.id}/${
                        this.thread?.id ?? ""
                    }`,
                    {
                        channel: this.channel,
                        draft: value,
                    }
                )
                .then(() => {
                    this.draftErrors = [];
                    if (this.hasContent !== this.conversation.hasDraft) {
                        this.$emit("drafted", this.hasContent);
                    }
                })
                .catch((error) => {
                    console.log(error.response.data);
                    this.draftErrors =
                        error.response.data.errors?.draft ??
                        error.response.data.errors?.message;
                });
        }, 400);

        if (this.$vuetify.breakpoint.smAndDown) {
            document.body.classList.add("dialog-open-fix");
        }

        if (this.conversation?.participants?.length) {
            this.form.recipients = this.conversation.participants.map(
                (data) => {
                    const { messagable_id, messagable_type, ...rest } = data;

                    return {
                        id: messagable_id,
                        type: messagable_type,
                        ...rest,
                    };
                }
            );
        }
    },
    beforeDestroy() {
        this.saveDraft(null, true);
        this.saveDraft = null;
        document.body.classList.remove("dialog-open-fix");
    },
    watch: {
        loading(val) {
            if (val) return;
            // Loading complete
        },
        latestMessage(val) {
            if (!val) return;
            if (!this.thread?.id) return;
            let messageSubject = val.subject ?? null;
            if (!messageSubject) return;
            this.form.subject = messageSubject.startsWith("Re:")
                ? messageSubject
                : `Re: ${messageSubject}`;
        },
        conversationUser: {
            handler: function (val) {
                if (!val) return;
                this.setDraft = val.draft?.length ?? 0;
                if (val.draft?.length) {
                    this.form.content = val.draft;
                } else {
                    this.form.content = null;
                    if (this.$refs.contentInput) {
                        this.$refs.contentInput.reset();
                    }
                }
            },
            immediate: true,
        },
    },
};
</script>

<style scoped>
#chat-box {
    position: relative;
}
</style>
